import React from "react";
import { Alert, Button, Card, Col, ListGroup, Row, Spinner } from "react-bootstrap";
import { IConversion } from "src/app-telehealth-demo/util/utils";
import { IFormData } from "../RemoteCareDemo";

interface ReviewStepProps {
    formData: IFormData;
    conversion: IConversion | null;
    onContinue: () => void;
    onBack: () => void;
    isSubmitting: boolean;
    error: string | null;
}

const ReviewAndSubmitStep: React.FC<ReviewStepProps> = ({
    formData,
    conversion,
    onContinue,
    onBack,
    isSubmitting,
    error,
}) => {
    // Format currency for display
    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    // Helper function to properly format field names
    const formatFieldName = (field: string) => {
        const nameWithoutPrefix = field.split("_").slice(1).join(" ");
        return nameWithoutPrefix
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Helper function to format field values
    const formatFieldValue = (value: any) => {
        if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        }
        if (value === true || value === "true") return "Yes";
        if (value === false || value === "false") return "No";
        return value;
    };

    // Group medical questions by category
    const groupMedicalQuestions = (prefillFields: any) => {
        const result: { [key: string]: { [key: string]: any } } = {};
        Object.entries(prefillFields).forEach(([field, value]) => {
            // Skip empty values
            if (value === "" || value === null || value === undefined) return;
            const prefix = field.split("_")[0];
            if (!result[prefix]) {
                result[prefix] = {};
            }
            result[prefix][field] = value;
        });
        return result;
    };

    const calculateTotal = () => {
        if (!conversion) return 0;
        return conversion.services
            .filter((service) => formData.selectedServiceIds.includes(service.careServiceId))
            .reduce((total, service) => total + service.costInCents, 0);
    };

    return (
        <div className="review-step">
            <h2 className="mb-4">Review Your Information</h2>
            <p className="text-muted mb-4">
                Please review your information before proceeding to payment.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Patient Information */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Personal Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <p className="mb-1">
                                <strong>Name:</strong> {formData.patientInfo.firstName}{" "}
                                {formData.patientInfo.lastName}
                            </p>
                            <p className="mb-1">
                                <strong>Date of Birth:</strong> {formData.patientInfo.dateOfBirth}
                            </p>
                            <p className="mb-1">
                                <strong>Email:</strong> {formData.patientInfo.email}
                            </p>
                            <p className="mb-1">
                                <strong>Phone:</strong> {formData.patientInfo.phone}
                            </p>
                        </Col>
                        <Col md={6}>
                            <p className="mb-1">
                                <strong>Address:</strong> {formData.patientInfo.address}
                                {formData.patientInfo.address2 &&
                                    `, ${formData.patientInfo.address2}`}
                            </p>
                            <p className="mb-1">
                                <strong>City:</strong> {formData.patientInfo.city}
                            </p>
                            <p className="mb-1">
                                <strong>State:</strong> {formData.patientInfo.state}
                            </p>
                            <p className="mb-1">
                                <strong>ZIP Code:</strong> {formData.patientInfo.zip}
                            </p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Medical History & Lifestyle */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Medical History & Lifestyle</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {Object.entries(groupMedicalQuestions(formData.prefillFields)).map(
                            ([category, fields]) => (
                                <Col md={6} key={category} className="mb-3">
                                    <h6 className="border-bottom pb-2 mb-3">
                                        {category === "mh"
                                            ? "Medical History"
                                            : category === "cma"
                                              ? "Medications & Allergies"
                                              : category === "ld"
                                                ? "Lifestyle"
                                                : "Other Information"}
                                    </h6>
                                    {Object.entries(fields).map(([field, value]) => (
                                        <p key={field} className="mb-1">
                                            <strong>{formatFieldName(field)}:</strong>{" "}
                                            {formatFieldValue(value)}
                                        </p>
                                    ))}
                                </Col>
                            ),
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {/* Selected Services */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Selected Services</h5>
                </Card.Header>
                <Card.Body>
                    {conversion && conversion.services && formData.selectedServiceIds.length > 0 ? (
                        <>
                            <ListGroup className="mb-3">
                                {conversion.services
                                    .filter((service) =>
                                        formData.selectedServiceIds.includes(service.careServiceId),
                                    )
                                    .map((service) => (
                                        <ListGroup.Item
                                            key={service.id}
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <span>{service.careServiceName}</span>
                                                {service.requiresLab && (
                                                    <span className="badge bg-info ms-2">
                                                        Requires Lab
                                                    </span>
                                                )}
                                            </div>
                                            <span className="fw-bold">
                                                {formatCurrency(service.costInCents)}
                                            </span>
                                        </ListGroup.Item>
                                    ))}
                            </ListGroup>
                            <div className="bg-light p-3 rounded">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Total:</h5>
                                    <h5 className="mb-0">{formatCurrency(calculateTotal())}</h5>
                                </div>
                            </div>
                        </>
                    ) : (
                        <p className="mb-0">No services selected</p>
                    )}
                </Card.Body>
            </Card>

            {/* Provider and Location */}
            {conversion && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Provider & Location</h5>
                    </Card.Header>
                    <Card.Body>
                        <p className="mb-1">
                            <strong>Provider:</strong> {conversion.practitionerName}
                        </p>
                        <p className="mb-1">
                            <strong>Location:</strong> {conversion.locationName}
                        </p>
                        <p className="mb-0">
                            <strong>Address:</strong> {conversion.locationAddress.fullAddress}
                        </p>
                    </Card.Body>
                </Card>
            )}

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={onBack} disabled={isSubmitting}>
                    Back
                </Button>
                <Button variant="primary" onClick={onContinue} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Submitting...
                        </>
                    ) : (
                        "Proceed to Payment"
                    )}
                </Button>
            </div>
        </div>
    );
};

export default ReviewAndSubmitStep;
